import { setCookie } from "@/hooks/cookie-helper";
import { createContext, useContext, useEffect, useState } from "react";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: ThemeScheme;
};

export const THEME_KEY = "NETTPAY_PORTAL_THEME_KEY";
// const storedTheme: ThemeScheme = localStorage.getItem(THEME_KEY) as ThemeScheme || "dark";
const storedTheme: ThemeScheme = "light";

type ThemeProviderState = {
  theme: ThemeScheme;
  setTheme: (theme: ThemeScheme) => void;
};

const initialState: ThemeProviderState = {
  theme: storedTheme,
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = "light",
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<ThemeScheme>(defaultTheme);

  useEffect(() => {
    const root = window.document.documentElement;

    root.classList.remove("light", "dark");

    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light";

      root.classList.add(systemTheme);
      return;
    }

    if (["system", "dark", "light"].includes(theme)) {
      setCookie({ name: THEME_KEY, value: theme, days: 3565 });
      root.classList.add(theme);
    }
  }, [theme]);

  const value = {
    theme,
    setTheme: (theme: ThemeScheme) => {
      localStorage.setItem(THEME_KEY, theme);
      setTheme(theme);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");

  return context;
};
